"use client";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const experiences = [
  {
    id: 1,
    title: "Alobha Technologies",
    role: "Full Stack Developer",
    time: "May 2026 - Present",
    link: "https://alobhatechnologies.com/",
    description: [
      {
        point:
          "Built and integrated secure REST APIs, optimized database queries, and implemented authentication and role-based access control, improving application performance and security.",
      },
      {
        point:
          "Collaborated with cross-functional teams to develop new features, fix production issues, and deploy applications using modern Git workflows and Agile practices.",
      },
    ],
  },
  {
    id: 2,
    title: "JIT",
    role: "Full Stack Developer",
    time: "December 2024 - May 2026",
    link: "https://www.jewarinternational.com/",
    description: [
      {
        point:
          "Developed a responsive UI using React.js, Tailwind CSS, and managed state with Context API delivering a smooth user experience across devices.",
      },
      {
        point:
          "Implemented real-time chat with Socket.io, added push notifications using OneSignal, and integrated data scraping from 7 countries via Puppeteer, visualized using Chart.js.",
      },
      {
        point:
          "Built and connected RESTful APIs with Node.js, Express.js, and MongoDB, optimizing performance, reducing load time by 20%, and improving data rendering efficiency.",
      },
    ],
  },
  {
    id: 3,
    title: "Innovixion Tech",
    role: "Full Stack Developer",
    time: "August 2024 - November 2024",
    link: "https://www.innovixiontech.in/",
    description: [
      {
        point:
          "Developed a full-stack, responsive learning platform using React.js, Node.js, Express.js, and Tailwind CSS, featuring seamless video playback, course tracking, and a modern UI/UX experience.",
      },
      {
        point:
          "Integrated Razorpay for secure payments and Cloudinary for media upload, storage, and optimized content delivery, enhancing the platform's performance and user interaction.",
      },
      {
        point: "Collaborated with team for scalable deployment.",
      },
    ],
  },
  {
    id: 4,
    title: "ICT Academy",
    role: "Full Stack Developer (Internship)",
    time: "August 2023 - September 2023",
    link: "#",
    description: [
      {
        point:
          "Assisted in software development tasks, contributing to the implementation of React.js projects.",
      },
      {
        point:
          "Collaborated with the team to optimize database queries, improving data retrieval speed.",
      },
      {
        point:
          "Gained hands-on experience in back-end development and API integrations.",
      },
    ],
  },
];

const Experience = () => {
  const [active, setActive] = useState(1);
  const activeExp = experiences.find((item) => item.id === active);

  // Optional safety guard (shouldn't be necessary, but best practice)
  if (!activeExp) return null;
  const { role, title, time, description, link } = activeExp;

  return (
    <div name="Experience" className="h-auto bg-[var(--bg-primary)] flex items-center justify-center transition-colors duration-300 px-4 md:px-[10%] pb-20 pt-10 md:pb-20 lg:pb-28 xl:pb-36 text-[var(--text-primary)] relative overflow-hidden">
      {/* Ambient background glow circles */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-[var(--accent)] opacity-[0.07] rounded-full blur-3xl pointer-events-none -z-0" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-[var(--accent)] opacity-[0.04] rounded-full blur-3xl pointer-events-none -z-0" />

      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        viewport={{ once: false, amount: 0.15 }}
        className="w-full md:w-[60%] md:h-[60vh] h-auto relative z-10"
      >
        {/* Heading */}
        <div className="flex items-center font-mono text-xl md:text-2xl font-bold">
          <span className="text-[var(--accent)]">02.</span>
          <h1 className="ml-2 text-xl md:text-3xl lg:text-4xl">
            Where I’ve Worked
          </h1>
          <div className="flex-grow h-[1px] bg-[var(--border)] ml-4"></div>
        </div>

        {/* Content */}
        <div className="my-10 flex">
          <div
            className="w-[30%] md:w-[20%] flex flex-col border-l border-[var(--border)] relative"
            role="tablist"
            aria-orientation="vertical"
          >
            {experiences.map((item) => (
              <button
                key={item.id}
                onClick={() => setActive(item.id)}
                role="tab"
                aria-selected={active === item.id}
                tabIndex={0}
                className={`px-4 py-3 text-left font-mono text-sm md:text-base transition-all duration-300 border-l-2 relative overflow-hidden
        ${active === item.id
                    ? "text-[var(--accent)] border-[var(--accent)] bg-[var(--bg-secondary)] font-semibold"
                    : "text-[var(--text-muted)] border-transparent hover:text-[var(--accent)] hover:bg-[var(--bg-secondary)]/50"
                  }`}
              >
                <span className="relative z-10">{item.title}</span>
                {active === item.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 bg-[var(--accent)]/10 rounded-md"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Details */}
          <div className="w-full pl-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.4 }}
                className="bg-[var(--bg-secondary)] p-5 rounded-lg shadow-md"
              >
                <h2 className="group text-lg md:text-xl font-semibold inline-block">
                  {role}{" "}
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="relative text-[var(--accent)] cursor-pointer transition"
                  >
                    @ {title}
                    <span className="absolute left-0 -bottom-1 w-0 h-[1px] bg-[var(--accent)] transition-all duration-300 group-hover:w-full"></span>
                  </a>
                </h2>

                <p className="text-sm text-[var(--text-muted)] my-2">{time}</p>
                <ul className="space-y-3">
                  {description.map((desc, idx) => (
                    <motion.li
                      key={idx}
                      className="flex items-start gap-2 text-[var(--text-muted)] leading-relaxed"
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <span className="text-[var(--accent)]">▹</span>
                      <span>{desc.point}</span>
                    </motion.li>
                  ))}
                </ul>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Experience;
